import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Component, inject, OnInit } from '@angular/core';
import { SOURCES, STATES, TIME_ZONES } from 'src/app/shared/constants';
import { UserService } from 'src/app/services/user/user.service';
import { CampaignService } from 'src/app/services/campaigns/campaign.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UtilsService } from 'src/app/services/utils/utils.service';
import Swal from 'sweetalert2';

@Component({
	selector: 'app-user-wizard',
	templateUrl: './user-wizard.component.html',
	styleUrls: ['./user-wizard.component.scss'],
})
export class UserWizardComponent implements OnInit {
	contactInfo: FormGroup;
	otherContactInfo: FormGroup;
	locationInfo: FormGroup;
	lmsInfo: FormGroup;
	loading = false;

	constructor(public activeModal: NgbActiveModal, public campaignSrv: CampaignService, private userSrv: UserService, private fb: FormBuilder, public utils: UtilsService) {
		this.contactInfo = this.fb.group({
			first_name: ['', Validators.required],
			last_name: ['', Validators.required],
			company_name: ['', Validators.required],
			email1: ['', [Validators.required, Validators.email]],
			mobile_phone: ['', Validators.pattern('^[0-9]{10}$')],
		});

		this.otherContactInfo = this.fb.group({
			email2: ['', [Validators.email]],
			email3: ['', [Validators.email]],
			phone_home: ['', Validators.pattern('^[0-9]{10}$')],
			phone_work: ['', Validators.pattern('^[0-9]{10}$')],
			phone_other: ['', Validators.pattern('^[0-9]{10}$')],
		});

		this.locationInfo = this.fb.group({
			address: ['', Validators.required],
			city: ['', Validators.required],
			state: ['', Validators.required],
			zip_code: ['', Validators.required],
			timezone: ['', Validators.required],
		});

		this.lmsInfo = this.fb.group({
			account_associated: ['', Validators.required],
			user_type: ['1', Validators.required],
			alias: [{ value: '', disabled: true }, Validators.required],
		});
	}

	user = {
		// Step 1 - Contact Info
		first_name: '',
		last_name: '',
		mobile_phone: '',
		email1: '',
		company_name: '',

		// Step 2 Others contact Info
		email2: '',
		email3: '',
		phone_home: '',
		phone_work: '',
		phone_other: '',

		// Step 3 Location
		address: '',
		city: '',
		state: '',
		zip_code: '',
		timezone: '',

		// Step 4 Internal LMS info
		user_type: '',
		alias: '',
		source: '',
		account_associated: '',
	};

	employeeUsers: any[] = [];
	campaigns = [];
	linkCampaign = false;

	states = STATES;

	timezones = TIME_ZONES;

	ngOnInit() {
		this.getEmployeeUsers();
		this.getCampaignsForSelect();
	}

	step = 1;
	modalTitles = ['Contact Information', 'Other Contact Information', 'Location', 'Internal LMS Information'];

	next() {
		if (this.step === 1 && !this.contactInfo.valid) {
			Object.keys(this.contactInfo.controls).forEach((key) => {
				const control = this.contactInfo.get(key);
				control?.markAsTouched();
			});
			return;
		}

		if (this.step === 2 && !this.otherContactInfo.valid) {
			Object.keys(this.otherContactInfo.controls).forEach((key) => {
				const control = this.otherContactInfo.get(key);
				control?.markAsTouched();
			});
			return;
		}

		if (this.step === 3 && !this.locationInfo.valid) {
			Object.keys(this.locationInfo.controls).forEach((key) => {
				const control = this.locationInfo.get(key);
				control?.markAsTouched();
			});
			return;
		}

		// Sync values with user object based on current step
		if (this.step === 1) {
			const formValues = this.contactInfo.value;
			Object.keys(formValues).forEach((key) => {
				this.user[key] = formValues[key];
			});
		} else if (this.step === 2) {
			const formValues = this.otherContactInfo.value;
			Object.keys(formValues).forEach((key) => {
				this.user[key] = formValues[key];
			});
		} else if (this.step === 3) {
			const formValues = this.locationInfo.value;
			Object.keys(formValues).forEach((key) => {
				this.user[key] = formValues[key];
			});
		}

		if (this.step < 4) {
			this.step++;
		}
	}

	prev() {
		if (this.step > 1) {
			this.step--;
		}
	}

	public getEmployeeUsers() {
		this.employeeUsers = [];
		this.userSrv.getActiveEmployees().subscribe((data: any) => {
			// TODO: MANAGE REsponse
			if (data.body) {
				if (data.body && data.body.length && !data.error) {
					this.employeeUsers = data.body;
				} else if (!data.success) {
					this.employeeUsers = [];
				}
			} else {
				this.employeeUsers = [];
			}
		});
	}

	getCampaignsForSelect() {
		this.campaignSrv.getCampaignsForSelect().subscribe((data: any) => {
			this.campaigns = data.body;
		});
	}

	linkCampaignToggle() {
		this.linkCampaign = !this.linkCampaign;
		if (!this.linkCampaign) {
			this.user.alias = '';
			this.lmsInfo.patchValue({ alias: '' });
			this.lmsInfo.get('alias')?.disable();
		} else {
			this.lmsInfo.get('alias')?.enable();
			this.lmsInfo.get('alias')?.markAsUntouched();
		}
	}

	onCampaignSelect(event: any) {
		const selectedValue = event.target.value;
		this.user.alias = selectedValue;
		this.lmsInfo.patchValue({ alias: selectedValue });
	}

	validateStep4() {
		if (this.step === 4 && !this.lmsInfo.valid) {
			Object.keys(this.lmsInfo.controls).forEach((key) => {
				const control = this.lmsInfo.get(key);
				control?.markAsTouched();
			});
			return;
		}

		if (this.step === 4) {
			const formValues = this.lmsInfo.value;
			Object.keys(formValues).forEach((key) => {
				this.user[key] = formValues[key];
			});

			this.createUser();
		}
	}

	validateField(fieldName: string): boolean {
		const field = this.contactInfo.get(fieldName);
		return field ? field.valid && field.touched : false;
	}

	createUser() {
		const formsToCheck = [
			{ name: 'contactInfo', group: this.contactInfo },
			{ name: 'otherContactInfo', group: this.otherContactInfo },
			{ name: 'locationInfo', group: this.locationInfo },
			{ name: 'lmsInfo', group: this.lmsInfo },
		];

		formsToCheck.forEach(({ name, group }) => {
			Object.keys(group.controls).forEach((field) => {
				const control = group.get(field);
				if (control && control.invalid) {
					console.info(`${name} - Field '${field}' is invalid:`, control.errors);
				}
			});
		});

		if (this.contactInfo.valid && this.otherContactInfo.valid && this.locationInfo.valid && this.lmsInfo.valid) {
			this.loading = true;
			this.userSrv.createSingleUser(this.user).subscribe(
				(data: any) => {
					this.loading = false;
					this.activeModal.close(data.body);
				},
				(err) => {
					console.error(err);
					Swal.fire({
						title: 'Error',
						icon: 'error',
						text: err.body.error,
						showCancelButton: false,
						confirmButtonText: 'Ok',
					});
					this.loading = false;
				}
			);
		} else {
			Swal.fire({
				title: 'Error',
				icon: 'error',
				text: 'Please validate all required fields in all steps.',
				showCancelButton: false,
				confirmButtonText: 'Ok',
			});
		}
	}
}
